import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { shipmentService } from '../services/shipmentService';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod'); // 'online' or 'cod'
  
  // Fixed shipping charge
  const SHIPPING_CHARGE = 100;
  
  // Calculate totals
  const subtotal = getCartTotal();
  const totalWithShipping = subtotal + SHIPPING_CHARGE;
  
  // Backend URL
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [shippingInfo, setShippingInfo] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const handleChange = (e) => {
    setShippingInfo({
      ...shippingInfo,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!shippingInfo.fullName || !shippingInfo.phone || !shippingInfo.address || !shippingInfo.city || !shippingInfo.pincode) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const handleCODOrder = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const orderData = {
        items: cartItems.map(item => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          weight: item.weight
        })),
        shippingInfo,
        totalAmount: totalWithShipping,
        shippingCharge: SHIPPING_CHARGE,
        paymentMethod: 'cod'
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/orders`,
        orderData,
        { withCredentials: true }
      );

      console.log('✅ Order created:', response.data);

      // Automatically create shipment
      try {
        console.log('🚚 Attempting to create shipment...');
        const shipmentResult = await shipmentService.createShipmentFromOrder(response.data, shippingInfo);
        console.log('✅ Shipment created:', shipmentResult);
        
        if (shipmentResult.waybill_number) {
          // Shipment successful - Delhivery activated
          toast.success(`✅ Order placed! Delivery started. Waybill: ${shipmentResult.waybill_number}`, {
            duration: 5000
          });
        } else {
          // Order saved but no waybill (shouldn't happen but handle gracefully)
          toast.success('✅ Order placed! Delivery will start soon.', {
            duration: 4000
          });
        }
      } catch (shipmentError) {
        console.error('❌ Shipment creation failed:', shipmentError);
        console.error('Error details:', shipmentError.response?.data);
        
        // Order is still saved! Just show delivery pending message
        const errorDetail = shipmentError.response?.data?.detail || '';
        
        if (errorDetail.includes('not enabled') || errorDetail.includes('non serviceable')) {
          // Pincode not enabled - but order is accepted
          toast.success('✅ Order placed! Delivery will start soon.', {
            duration: 4000
          });
          toast.info('Your pincode delivery will be arranged by our team.', {
            duration: 4000
          });
        } else {
          // Other error - still accept order
          toast.success('✅ Order placed! Delivery will start soon.', {
            duration: 4000
          });
        }
      }

      clearCart();
      navigate('/');
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Step 1: Create Razorpay order
      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/payment/create-order`,
        {
          amount: totalWithShipping * 100, // Convert to paise
          currency: 'INR'
        },
        { withCredentials: true }
      );

      const { orderId, amount, currency, key } = orderResponse.data;

      // Step 2: Initialize Razorpay
      const options = {
        key: key || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'Arihant Milk Products',
        description: 'Order Payment',
        order_id: orderId,
        handler: async function (response) {
          try {
            // Step 3: Verify payment
            await axios.post(
              `${BACKEND_URL}/api/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            // Step 4: Create order with payment info
            const orderData = {
              items: cartItems.map(item => ({
                productId: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                weight: item.weight
              })),
              shippingInfo,
              totalAmount: totalWithShipping,
              shippingCharge: SHIPPING_CHARGE,
              paymentMethod: 'online'
            };

            const orderRes = await axios.post(
              `${BACKEND_URL}/api/orders`,
              orderData,
              { withCredentials: true }
            );

            console.log('✅ Online order created:', orderRes.data);

            // Update order with payment details
            await axios.post(
              `${BACKEND_URL}/api/orders/complete-payment?order_id=${orderRes.data.id}&payment_id=${response.razorpay_payment_id}&razorpay_order_id=${response.razorpay_order_id}`,
              {},
              { withCredentials: true }
            );

            // Automatically create shipment
            try {
              console.log('🚚 Attempting to create shipment for online payment...');
              const shipmentResult = await shipmentService.createShipmentFromOrder(orderRes.data, shippingInfo);
              console.log('✅ Shipment created:', shipmentResult);
              
              if (shipmentResult.waybill_number) {
                // Shipment successful - Delhivery activated
                toast.success(`✅ Payment successful! Delivery started. Waybill: ${shipmentResult.waybill_number}`, {
                  duration: 5000
                });
              } else {
                // Order saved but no waybill
                toast.success('✅ Payment successful! Delivery will start soon.', {
                  duration: 4000
                });
              }
            } catch (shipmentError) {
              console.error('❌ Shipment creation failed:', shipmentError);
              console.error('Error details:', shipmentError.response?.data);
              
              // Payment successful, order saved - just show delivery pending
              const errorDetail = shipmentError.response?.data?.detail || '';
              
              if (errorDetail.includes('not enabled') || errorDetail.includes('non serviceable')) {
                toast.success('✅ Payment successful! Delivery will start soon.', {
                  duration: 4000
                });
                toast.info('Your pincode delivery will be arranged by our team.', {
                  duration: 4000
                });
              } else {
                toast.success('✅ Payment successful! Delivery will start soon.', {
                  duration: 4000
                });
              }
            }

            clearCart();
            navigate('/');
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: shippingInfo.fullName,
          email: shippingInfo.email,
          contact: shippingInfo.phone
        },
        theme: {
          color: '#f43f5e'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
        console.error('Payment failed:', response.error);
      });
      rzp.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = () => {
    if (paymentMethod === 'cod') {
      handleCODOrder();
    } else {
      handleOnlinePayment();
    }
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-rose-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Information Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-2xl font-bold text-rose-900 mb-6">Shipping Information</h2>
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={shippingInfo.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={shippingInfo.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={shippingInfo.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={shippingInfo.address}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={shippingInfo.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={shippingInfo.state}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-2">
                      Pincode *
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={shippingInfo.pincode}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-rose-900 mb-6">Payment Method</h2>
              <div className="space-y-4">
                <div
                  onClick={() => setPaymentMethod('cod')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    paymentMethod === 'cod'
                      ? 'border-rose-500 bg-rose-50'
                      : 'border-gray-200 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center">
                    <Banknote className="h-6 w-6 text-rose-600 mr-3" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Cash on Delivery</h3>
                      <p className="text-sm text-gray-600">Pay when you receive your order</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'cod' ? 'border-rose-500' : 'border-gray-300'
                      }`}
                    >
                      {paymentMethod === 'cod' && (
                        <div className="w-3 h-3 rounded-full bg-rose-500" />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => setPaymentMethod('online')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    paymentMethod === 'online'
                      ? 'border-rose-500 bg-rose-50'
                      : 'border-gray-200 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center">
                    <CreditCard className="h-6 w-6 text-rose-600 mr-3" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Online Payment</h3>
                      <p className="text-sm text-gray-600">Pay securely via Razorpay (Card/UPI/Netbanking)</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'online' ? 'border-rose-500' : 'border-gray-300'
                      }`}
                    >
                      {paymentMethod === 'online' && (
                        <div className="w-3 h-3 rounded-full bg-rose-500" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-20">
              <h2 className="text-2xl font-bold text-rose-900 mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-medium">₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping Charge</span>
                  <span className="text-rose-600 font-medium">₹{SHIPPING_CHARGE}</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-lg font-bold text-rose-900">
                  <span>Total</span>
                  <span>₹{totalWithShipping}</span>
                </div>
              </div>

              <Button
                onClick={handlePlaceOrder}
                disabled={loading}
                className="w-full bg-rose-500 hover:bg-rose-600 text-white py-6 text-lg"
              >
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    {paymentMethod === 'cod' ? (
                      <>
                        <Banknote className="mr-2 h-5 w-5" />
                        Place Order (COD)
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        Pay ₹{totalWithShipping}
                      </>
                    )}
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {paymentMethod === 'cod' 
                  ? 'You will pay when you receive your order'
                  : 'Secured by Razorpay payment gateway'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
